import type { ComponentProps } from 'react';
import { FieldInfo, useFieldContext, Select } from './shared';
import { Label } from '../label';
import { useQuery } from '@tanstack/react-query';
import { fetchCountryRegions } from '@worldcoo/monolith/forms/regions';
import { cn } from '../../lib/utils';

export default function ProvinceField({
	label,
	country,
	region,
	className,
	...props
}: ComponentProps<'select'> & {
	label: string;
	country: string | undefined;
	region: string | undefined;
	variant?: 'default' | 'outline';
}) {
	const field = useFieldContext<string>();
	const { data: regions = [] } = useQuery({
		queryKey: ['countries', country],
		queryFn: !country ? () => [] : () => fetchCountryRegions(country).then((data) => data ?? []),
	});

	const provinces = regions.find((r) => r.code === region)?.provinces ?? [];

	return (
		<div className={cn(className, (!country || !region) && 'hidden')}>
			<div className="text-muted-foreground relative">
				{label && (
					<Label
						data-slot="form-label"
						data-error={field.state.meta.isTouched && !field.state.meta.isValid}
						className="data-[error=true]:text-destructive floating pointer-events-none w-full truncate px-3.5 py-[17px]"
						htmlFor={field.name}
					>
						{label}
					</Label>
				)}
				<Select
					id={field.name}
					name={field.name}
					value={field.state.value}
					onBlur={field.handleBlur}
					onChange={(e) => field.handleChange(e.target.value)}
					{...props}
				>
					<option value=""></option>
					{provinces.map(({ code, name }) => (
						<option key={code} value={code}>
							{name}
						</option>
					))}
				</Select>
			</div>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
